'use client';

import { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import type { ArchiMateElement, ArchiMateRelationship, ElementType, RelationshipType } from '@/lib/archimate/types';
import { getElementMetadata } from '@/lib/archimate/elements';
import { elementContains } from '@/lib/archimate/layout';

// Helper: Get point on box edge given angle
function getBoxEdgePoint(cx: number, cy: number, w: number, h: number, angle: number): { x: number; y: number } {
  // Calculate intersection with box edge
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);

  // Check which edge the line intersects
  const tx = w / Math.abs(cos);
  const ty = h / Math.abs(sin);

  if (tx < ty) {
    // Intersects left or right edge
    return {
      x: cx + w * Math.sign(cos),
      y: cy + w * Math.tan(angle) * Math.sign(cos)
    };
  } else {
    // Intersects top or bottom edge
    return {
      x: cx + h / Math.tan(angle) * Math.sign(sin),
      y: cy + h * Math.sign(sin)
    };
  }
}

// Get ArchiMate relationship visual style
function getRelationshipStyle(type: RelationshipType): { dashArray: string; marker: string } {
  switch (type) {
    case 'composition':
      return { dashArray: '', marker: 'arrow-filled' };
    case 'aggregation':
      return { dashArray: '', marker: 'arrow-hollow' };
    case 'assignment':
    case 'realization':
      return { dashArray: '', marker: 'arrow-open' };
    case 'serving':
    case 'access':
      return { dashArray: '', marker: 'arrow-open' };
    case 'influence':
      return { dashArray: '4,4', marker: 'arrow-open' };
    case 'association':
      return { dashArray: '', marker: 'none' };
    case 'triggering':
    case 'flow':
      return { dashArray: '4,2', marker: 'arrow-open' };
    case 'specialization':
      return { dashArray: '', marker: 'arrow-hollow' };
    default:
      return { dashArray: '', marker: 'arrow-open' };
  }
}

// Helper: Render ArchiMate icon as SVG
function renderArchiMateIcon(g: any, type: ElementType, x: number, y: number, size = 14, iconSymbol?: string) {
  const iconG = g.append('g')
    .attr('class', 'element-icon')
    .attr('transform', `translate(${x}, ${y})`);

  // Render based on element type
  if (type.includes('actor')) {
    // Stick figure
    iconG.append('circle').attr('cx', size/2).attr('cy', size*0.3).attr('r', size*0.15).attr('fill', 'none').attr('stroke', '#000').attr('stroke-width', 1);
    iconG.append('line').attr('x1', size/2).attr('y1', size*0.45).attr('x2', size/2).attr('y2', size*0.7).attr('stroke', '#000').attr('stroke-width', 1);
    iconG.append('line').attr('x1', size*0.25).attr('y1', size*0.55).attr('x2', size*0.75).attr('y2', size*0.55).attr('stroke', '#000').attr('stroke-width', 1);
    iconG.append('line').attr('x1', size/2).attr('y1', size*0.7).attr('x2', size*0.3).attr('y2', size*0.95).attr('stroke', '#000').attr('stroke-width', 1);
    iconG.append('line').attr('x1', size/2).attr('y1', size*0.7).attr('x2', size*0.7).attr('y2', size*0.95).attr('stroke', '#000').attr('stroke-width', 1);
  } else if (type.includes('role')) {
    // Circle with vertical line
    iconG.append('circle').attr('cx', size/2).attr('cy', size/2).attr('r', size*0.35).attr('fill', 'none').attr('stroke', '#000').attr('stroke-width', 1.5);
    iconG.append('line').attr('x1', size/2).attr('y1', size*0.15).attr('x2', size/2).attr('y2', size*0.85).attr('stroke', '#000').attr('stroke-width', 1.5);
  } else if (type.includes('collaboration')) {
    // Interlocking circles
    iconG.append('circle').attr('cx', size*0.35).attr('cy', size/2).attr('r', size*0.25).attr('fill', 'none').attr('stroke', '#000').attr('stroke-width', 1.5);
    iconG.append('circle').attr('cx', size*0.65).attr('cy', size/2).attr('r', size*0.25).attr('fill', 'none').attr('stroke', '#000').attr('stroke-width', 1.5);
  } else if (type.includes('interface')) {
    // Circle with dot (lollipop)
    iconG.append('circle').attr('cx', size/2).attr('cy', size/2).attr('r', size*0.35).attr('fill', 'none').attr('stroke', '#000').attr('stroke-width', 1.5);
    iconG.append('circle').attr('cx', size/2).attr('cy', size/2).attr('r', size*0.12).attr('fill', '#000');
  } else if (type.includes('process')) {
    // Arrow
    iconG.append('path').attr('d', `M ${size*0.1} ${size/2} L ${size*0.7} ${size/2} M ${size*0.6} ${size*0.3} L ${size*0.7} ${size/2} L ${size*0.6} ${size*0.7}`).attr('fill', 'none').attr('stroke', '#000').attr('stroke-width', 1.5).attr('stroke-linecap', 'round');
  } else if (type.includes('function')) {
    // Chevron/house
    iconG.append('path').attr('d', `M ${size*0.2} ${size*0.7} L ${size/2} ${size*0.25} L ${size*0.8} ${size*0.7}`).attr('fill', 'none').attr('stroke', '#000').attr('stroke-width', 1.5);
  } else if (type.includes('interaction')) {
    // Half circle
    iconG.append('path').attr('d', `M ${size*0.2} ${size*0.3} Q ${size/2} ${size*0.8} ${size*0.8} ${size*0.3}`).attr('fill', 'none').attr('stroke', '#000').attr('stroke-width', 1.5);
  } else if (type.includes('component')) {
    // Stacked rectangles
    iconG.append('rect').attr('x', size*0.25).attr('y', size*0.15).attr('width', size*0.5).attr('height', size*0.25).attr('fill', 'none').attr('stroke', '#000').attr('stroke-width', 1);
    iconG.append('rect').attr('x', size*0.25).attr('y', size*0.45).attr('width', size*0.5).attr('height', size*0.25).attr('fill', 'none').attr('stroke', '#000').attr('stroke-width', 1);
  } else if (type.includes('artifact') || type.includes('deliverable')) {
    // Document with folded corner
    iconG.append('path').attr('d', `M ${size*0.25} ${size*0.15} L ${size*0.65} ${size*0.15} L ${size*0.75} ${size*0.25} L ${size*0.75} ${size*0.85} L ${size*0.25} ${size*0.85} Z`).attr('fill', 'none').attr('stroke', '#000').attr('stroke-width', 1);
    iconG.append('path').attr('d', `M ${size*0.65} ${size*0.15} L ${size*0.65} ${size*0.25} L ${size*0.75} ${size*0.25}`).attr('fill', 'none').attr('stroke', '#000').attr('stroke-width', 1);
  } else if (type.includes('node')) {
    // Rectangle (box)
    iconG.append('rect').attr('x', size*0.2).attr('y', size*0.25).attr('width', size*0.6).attr('height', size*0.5).attr('fill', 'none').attr('stroke', '#000').attr('stroke-width', 1.5);
  } else if (type.includes('device')) {
    // Monitor/screen icon
    iconG.append('rect').attr('x', size*0.2).attr('y', size*0.2).attr('width', size*0.6).attr('height', size*0.5).attr('fill', 'none').attr('stroke', '#000').attr('stroke-width', 1.5);
    iconG.append('rect').attr('x', size*0.35).attr('y', size*0.7).attr('width', size*0.3).attr('height', size*0.1).attr('fill', 'none').attr('stroke', '#000').attr('stroke-width', 1);
  } else if (type.includes('system-software')) {
    // Circle
    iconG.append('circle').attr('cx', size/2).attr('cy', size/2).attr('r', size*0.35).attr('fill', 'none').attr('stroke', '#000').attr('stroke-width', 1.5);
  } else if (type.includes('equipment')) {
    // Gear/cog shape (simplified)
    iconG.append('circle').attr('cx', size/2).attr('cy', size/2).attr('r', size*0.3).attr('fill', 'none').attr('stroke', '#000').attr('stroke-width', 1.5);
    iconG.append('circle').attr('cx', size/2).attr('cy', size/2).attr('r', size*0.15).attr('fill', 'none').attr('stroke', '#000').attr('stroke-width', 1);
  } else if (type.includes('facility')) {
    // Building/house
    iconG.append('path').attr('d', `M ${size*0.2} ${size*0.5} L ${size/2} ${size*0.2} L ${size*0.8} ${size*0.5} L ${size*0.8} ${size*0.8} L ${size*0.2} ${size*0.8} Z`).attr('fill', 'none').attr('stroke', '#000').attr('stroke-width', 1.5);
  } else if (type.includes('work-package')) {
    // Package/box icon
    iconG.append('rect').attr('x', size*0.2).attr('y', size*0.2).attr('width', size*0.6).attr('height', size*0.6).attr('fill', 'none').attr('stroke', '#000').attr('stroke-width', 1.5);
    iconG.append('line').attr('x1', size*0.2).attr('y1', size*0.4).attr('x2', size*0.8).attr('y2', size*0.4).attr('stroke', '#000').attr('stroke-width', 1);
  } else if (type.includes('stakeholder')) {
    // Multiple people (two stick figures)
    iconG.append('circle').attr('cx', size*0.35).attr('cy', size*0.3).attr('r', size*0.12).attr('fill', 'none').attr('stroke', '#000').attr('stroke-width', 1);
    iconG.append('line').attr('x1', size*0.35).attr('y1', size*0.42).attr('x2', size*0.35).attr('y2', size*0.65).attr('stroke', '#000').attr('stroke-width', 1);
    iconG.append('circle').attr('cx', size*0.65).attr('cy', size*0.3).attr('r', size*0.12).attr('fill', 'none').attr('stroke', '#000').attr('stroke-width', 1);
    iconG.append('line').attr('x1', size*0.65).attr('y1', size*0.42).attr('x2', size*0.65).attr('y2', size*0.65).attr('stroke', '#000').attr('stroke-width', 1);
  } else if (type.includes('goal')) {
    // Target/bullseye
    iconG.append('circle').attr('cx', size/2).attr('cy', size/2).attr('r', size*0.35).attr('fill', 'none').attr('stroke', '#000').attr('stroke-width', 1.5);
    iconG.append('circle').attr('cx', size/2).attr('cy', size/2).attr('r', size*0.15).attr('fill', '#000');
  } else if (type.includes('requirement')) {
    // Checklist/document with lines
    iconG.append('rect').attr('x', size*0.25).attr('y', size*0.2).attr('width', size*0.5).attr('height', size*0.6).attr('fill', 'none').attr('stroke', '#000').attr('stroke-width', 1.5);
    iconG.append('line').attr('x1', size*0.35).attr('y1', size*0.4).attr('x2', size*0.65).attr('y2', size*0.4).attr('stroke', '#000').attr('stroke-width', 1);
    iconG.append('line').attr('x1', size*0.35).attr('y1', size*0.6).attr('x2', size*0.65).attr('y2', size*0.6).attr('stroke', '#000').attr('stroke-width', 1);
  } else if (type.includes('outcome')) {
    // Checkmark
    iconG.append('path').attr('d', `M ${size*0.2} ${size*0.5} L ${size*0.4} ${size*0.7} L ${size*0.8} ${size*0.3}`).attr('fill', 'none').attr('stroke', '#000').attr('stroke-width', 1.5).attr('stroke-linecap', 'round').attr('stroke-linejoin', 'round');
  } else if (type.includes('principle')) {
    // Balance/scales
    iconG.append('line').attr('x1', size/2).attr('y1', size*0.2).attr('x2', size/2).attr('y2', size*0.8).attr('stroke', '#000').attr('stroke-width', 1.5);
    iconG.append('line').attr('x1', size*0.2).attr('y1', size*0.4).attr('x2', size*0.8).attr('y2', size*0.4).attr('stroke', '#000').attr('stroke-width', 1.5);
  } else if (type.includes('constraint')) {
    // Lock
    iconG.append('rect').attr('x', size*0.3).attr('y', size*0.5).attr('width', size*0.4).attr('height', size*0.3).attr('fill', 'none').attr('stroke', '#000').attr('stroke-width', 1.5);
    iconG.append('path').attr('d', `M ${size*0.35} ${size*0.5} L ${size*0.35} ${size*0.4} Q ${size*0.35} ${size*0.25} ${size/2} ${size*0.25} Q ${size*0.65} ${size*0.25} ${size*0.65} ${size*0.4} L ${size*0.65} ${size*0.5}`).attr('fill', 'none').attr('stroke', '#000').attr('stroke-width', 1.5);
  } else if (type.includes('driver')) {
    // Lightning bolt
    iconG.append('path').attr('d', `M ${size*0.55} ${size*0.2} L ${size*0.35} ${size*0.5} L ${size*0.5} ${size*0.5} L ${size*0.35} ${size*0.8} L ${size*0.65} ${size*0.45} L ${size*0.5} ${size*0.45} Z`).attr('fill', 'none').attr('stroke', '#000').attr('stroke-width', 1.5);
  } else if (type.includes('assessment')) {
    // Chart/graph
    iconG.append('line').attr('x1', size*0.2).attr('y1', size*0.7).attr('x2', size*0.4).attr('y2', size*0.5).attr('stroke', '#000').attr('stroke-width', 1.5);
    iconG.append('line').attr('x1', size*0.4).attr('y1', size*0.5).attr('x2', size*0.6).attr('y2', size*0.6).attr('stroke', '#000').attr('stroke-width', 1.5);
    iconG.append('line').attr('x1', size*0.6).attr('y1', size*0.6).attr('x2', size*0.8).attr('y2', size*0.3).attr('stroke', '#000').attr('stroke-width', 1.5);
  } else if (type.includes('value')) {
    // Diamond
    iconG.append('path').attr('d', `M ${size/2} ${size*0.2} L ${size*0.8} ${size/2} L ${size/2} ${size*0.8} L ${size*0.2} ${size/2} Z`).attr('fill', 'none').attr('stroke', '#000').attr('stroke-width', 1.5);
  } else if (type.includes('plateau')) {
    // Horizontal bar (level)
    iconG.append('line').attr('x1', size*0.2).attr('y1', size/2).attr('x2', size*0.8).attr('y2', size/2).attr('stroke', '#000').attr('stroke-width', 2);
  } else if (type.includes('gap')) {
    // Dotted line (gap)
    iconG.append('line').attr('x1', size*0.2).attr('y1', size/2).attr('x2', size*0.8).attr('y2', size/2).attr('stroke', '#000').attr('stroke-width', 1.5).attr('stroke-dasharray', '2,2');
  } else if (type.includes('service')) {
    // Circle with notch (service port)
    iconG.append('circle').attr('cx', size/2).attr('cy', size/2).attr('r', size*0.35).attr('fill', 'none').attr('stroke', '#000').attr('stroke-width', 1.5);
    iconG.append('rect').attr('x', size*0.48).attr('y', size*0.15).attr('width', size*0.18).attr('height', size*0.2).attr('fill', 'white').attr('stroke', '#000').attr('stroke-width', 1);
  } else if (type.includes('event')) {
    // Circle with clock hands
    iconG.append('circle').attr('cx', size/2).attr('cy', size/2).attr('r', size*0.35).attr('fill', 'none').attr('stroke', '#000').attr('stroke-width', 1.5);
    iconG.append('line').attr('x1', size/2).attr('y1', size/2).attr('x2', size/2).attr('y2', size*0.25).attr('stroke', '#000').attr('stroke-width', 1.5);
    iconG.append('line').attr('x1', size/2).attr('y1', size/2).attr('x2', size*0.65).attr('y2', size*0.45).attr('stroke', '#000').attr('stroke-width', 1.5);
  } else if (type.includes('object') || type.includes('data')) {
    // No icon for passive data objects (ArchiMate standard)
  } else if (type.includes('meaning')) {
    // Speech bubble
    iconG.append('circle').attr('cx', size*0.45).attr('cy', size*0.4).attr('r', size*0.3).attr('fill', 'none').attr('stroke', '#000').attr('stroke-width', 1.5);
    iconG.append('path').attr('d', `M ${size*0.4} ${size*0.65} L ${size*0.3} ${size*0.85} L ${size*0.5} ${size*0.7}`).attr('fill', 'none').attr('stroke', '#000').attr('stroke-width', 1.5);
  } else if (type.includes('location')) {
    // Pin/location marker
    iconG.append('circle').attr('cx', size/2).attr('cy', size*0.4).attr('r', size*0.2).attr('fill', 'none').attr('stroke', '#000').attr('stroke-width', 1.5);
    iconG.append('path').attr('d', `M ${size/2} ${size*0.6} L ${size/2} ${size*0.85}`).attr('stroke', '#000').attr('stroke-width', 1.5);
  } else if (type.includes('path')) {
    // Bidirectional arrow
    iconG.append('path').attr('d', `M ${size*0.1} ${size/2} L ${size*0.9} ${size/2}`).attr('stroke', '#000').attr('stroke-width', 1.5);
    iconG.append('path').attr('d', `M ${size*0.2} ${size*0.35} L ${size*0.1} ${size/2} L ${size*0.2} ${size*0.65}`).attr('fill', 'none').attr('stroke', '#000').attr('stroke-width', 1.5);
    iconG.append('path').attr('d', `M ${size*0.8} ${size*0.35} L ${size*0.9} ${size/2} L ${size*0.8} ${size*0.65}`).attr('fill', 'none').attr('stroke', '#000').attr('stroke-width', 1.5);
  } else if (type.includes('network')) {
    // Hash/grid pattern
    iconG.append('line').attr('x1', size*0.2).attr('y1', size*0.35).attr('x2', size*0.8).attr('y2', size*0.35).attr('stroke', '#000').attr('stroke-width', 1);
    iconG.append('line').attr('x1', size*0.2).attr('y1', size*0.65).attr('x2', size*0.8).attr('y2', size*0.65).attr('stroke', '#000').attr('stroke-width', 1);
    iconG.append('line').attr('x1', size*0.35).attr('y1', size*0.2).attr('x2', size*0.35).attr('y2', size*0.8).attr('stroke', '#000').attr('stroke-width', 1);
    iconG.append('line').attr('x1', size*0.65).attr('y1', size*0.2).attr('x2', size*0.65).attr('y2', size*0.8).attr('stroke', '#000').attr('stroke-width', 1);
  } else if (type.includes('material')) {
    // Triangle
    iconG.append('path').attr('d', `M ${size/2} ${size*0.2} L ${size*0.8} ${size*0.8} L ${size*0.2} ${size*0.8} Z`).attr('fill', 'none').attr('stroke', '#000').attr('stroke-width', 1.5);
  } else if (type.includes('capability')) {
    // Hexagon
    const r = size*0.35;
    const points = [];
    for (let i = 0; i < 6; i++) {
      const angle = (Math.PI / 3) * i - Math.PI / 2;
      points.push(`${size/2 + r * Math.cos(angle)},${size/2 + r * Math.sin(angle)}`);
    }
    iconG.append('polygon').attr('points', points.join(' ')).attr('fill', 'none').attr('stroke', '#000').attr('stroke-width', 1.5);
  } else if (type.includes('resource')) {
    // Square/box
    iconG.append('rect').attr('x', size*0.25).attr('y', size*0.25).attr('width', size*0.5).attr('height', size*0.5).attr('fill', 'none').attr('stroke', '#000').attr('stroke-width', 1.5);
  } else if (type.includes('value-stream') || type.includes('course-of-action')) {
    // Same as process - arrow
    iconG.append('path').attr('d', `M ${size*0.1} ${size/2} L ${size*0.7} ${size/2} M ${size*0.6} ${size*0.3} L ${size*0.7} ${size/2} L ${size*0.6} ${size*0.7}`).attr('fill', 'none').attr('stroke', '#000').attr('stroke-width', 1.5).attr('stroke-linecap', 'round');
  } else if (type.includes('business-object') || type.includes('data-object')) {
    // Rectangle (passive data)
    iconG.append('rect').attr('x', size*0.25).attr('y', size*0.3).attr('width', size*0.5).attr('height', size*0.4).attr('fill', 'none').attr('stroke', '#000').attr('stroke-width', 1.5);
  } else if (type.includes('contract')) {
    // Document with signature line
    iconG.append('rect').attr('x', size*0.25).attr('y', size*0.2).attr('width', size*0.5).attr('height', size*0.6).attr('fill', 'none').attr('stroke', '#000').attr('stroke-width', 1.5);
    iconG.append('line').attr('x1', size*0.3).attr('y1', size*0.7).attr('x2', size*0.7).attr('y2', size*0.7).attr('stroke', '#000').attr('stroke-width', 1).attr('stroke-dasharray', '2,1');
  } else if (type.includes('representation')) {
    // Wavy document
    iconG.append('rect').attr('x', size*0.25).attr('y', size*0.2).attr('width', size*0.5).attr('height', size*0.5).attr('fill', 'none').attr('stroke', '#000').attr('stroke-width', 1.5);
    iconG.append('path').attr('d', `M ${size*0.25} ${size*0.73} Q ${size*0.375} ${size*0.8} ${size/2} ${size*0.73} Q ${size*0.625} ${size*0.66} ${size*0.75} ${size*0.73}`).attr('fill', 'none').attr('stroke', '#000').attr('stroke-width', 1.5);
  } else if (type.includes('product')) {
    // Box with corner fold (same as folded shape)
    iconG.append('path').attr('d', `M ${size*0.25} ${size*0.2} L ${size*0.65} ${size*0.2} L ${size*0.75} ${size*0.3} L ${size*0.75} ${size*0.8} L ${size*0.25} ${size*0.8} Z`).attr('fill', 'none').attr('stroke', '#000').attr('stroke-width', 1.5);
    iconG.append('path').attr('d', `M ${size*0.65} ${size*0.2} L ${size*0.65} ${size*0.3} L ${size*0.75} ${size*0.3}`).attr('fill', 'none').attr('stroke', '#000').attr('stroke-width', 1.5);
  } else if (iconSymbol && iconSymbol.length > 0) {
    // Fallback: render as text for unicode symbols not yet implemented as SVG
    iconG.append('text')
      .text(iconSymbol)
      .attr('x', size/2)
      .attr('y', size/2)
      .attr('text-anchor', 'middle')
      .attr('dy', size*0.15)
      .attr('font-size', size*0.8)
      .attr('fill', '#000')
      .style('pointer-events', 'none');
  }
}

// Helper: Wrap text to fit width
function wrapText(text: string, maxWidth: number): string[] {
  const words = text.split(' ');
  const lines: string[] = [];
  let currentLine = '';

  words.forEach(word => {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    // Rough estimate: 7 pixels per character
    if (testLine.length * 7 > maxWidth && currentLine) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = testLine;
    }
  });

  if (currentLine) lines.push(currentLine);
  return lines.slice(0, 3); // Max 3 lines
}

// Helper: Adjust color brightness for 3D effect
function adjustColor(color: string, amount: number): string {
  const hex = color.replace('#', '');
  const r = Math.max(0, Math.min(255, parseInt(hex.substr(0, 2), 16) + amount));
  const g = Math.max(0, Math.min(255, parseInt(hex.substr(2, 2), 16) + amount));
  const b = Math.max(0, Math.min(255, parseInt(hex.substr(4, 2), 16) + amount));
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

// Helper: Calculate heatmap color for an element
function getHeatmapColor(
  elementId: string,
  heatmapType: 'cost' | 'security' | 'debt' | 'performance',
  metadata: Record<string, any>
): string | null {
  const elementMeta = metadata[elementId];
  if (!elementMeta) return null;

  switch (heatmapType) {
    case 'cost': {
      const cost = elementMeta.cost;
      if (!cost || !cost.totalCost) return null;
      const total = cost.totalCost;
      // Color scale: green (low) -> yellow (medium) -> red (high)
      if (total < 5000) return '#22c55e'; // green-500
      if (total < 20000) return '#eab308'; // yellow-500
      return '#ef4444'; // red-500
    }

    case 'security': {
      const security = elementMeta.security;
      if (!security) return null;
      const threatLevel = security.threatLevel || 'low';
      // Color scale by threat level
      if (threatLevel === 'low') return '#22c55e'; // green-500
      if (threatLevel === 'medium') return '#eab308'; // yellow-500
      return '#ef4444'; // red-500
    }

    case 'debt': {
      const debt = elementMeta.debt;
      if (!debt) return null;
      const severity = debt.severity || 'low';
      // Color scale by severity
      if (severity === 'low') return '#22c55e'; // green-500
      if (severity === 'medium') return '#eab308'; // yellow-500
      if (severity === 'high') return '#f97316'; // orange-500
      return '#ef4444'; // red-500 (critical)
    }

    case 'performance': {
      const perf = elementMeta.performance;
      if (!perf || !perf.slos || perf.slos.length === 0) return null;
      // Check if any SLO is violated
      const hasViolations = perf.slos.some((slo: any) => {
        if (!slo.currentValue || !slo.targetValue) return false;
        return slo.currentValue < slo.targetValue * 0.9; // 10% tolerance
      });
      // Color scale: green (good) -> yellow (warning) -> red (violation)
      if (hasViolations) return '#ef4444'; // red-500
      const hasWarnings = perf.slos.some((slo: any) => {
        if (!slo.currentValue || !slo.targetValue) return false;
        return slo.currentValue < slo.targetValue;
      });
      if (hasWarnings) return '#eab308'; // yellow-500
      return '#22c55e'; // green-500
    }

    default:
      return null;
  }
}

interface ArchiMateCanvasProps {
  elements: ArchiMateElement[];
  relationships: ArchiMateRelationship[];
  selectedElement?: ArchiMateElement | null;
  selectedElementIds?: string[];
  pendingConnection?: string | null;
  tool?: 'select' | 'connect';
  onSelectElement?: (element: ArchiMateElement) => void;
  onUpdateElement?: (id: string, updates: Partial<ArchiMateElement>) => void;
  onAddElement?: (element: Omit<ArchiMateElement, 'id'>) => void;
  onCanvasDrop?: (elementType: string, x: number, y: number, targetElementId?: string) => void;
  onContextMenu?: (x: number, y: number, type: 'element' | 'relationship', id: string) => void;
  onDoubleClick?: (element: ArchiMateElement) => void;
  activeHeatmap?: 'cost' | 'security' | 'debt' | 'performance' | null;
  elementMetadata?: Record<string, any>;
}

export function ArchiMateCanvas({
  elements,
  relationships,
  selectedElement,
  selectedElementIds = [],
  pendingConnection,
  tool = 'select',
  onSelectElement,
  onUpdateElement,
  onCanvasDrop,
  onContextMenu,
  onDoubleClick,
  activeHeatmap,
  elementMetadata = {},
}: ArchiMateCanvasProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const gRef = useRef<SVGGElement>(null);
  const transformRef = useRef<d3.ZoomTransform>(d3.zoomIdentity);
  const gRefForUpdate = useRef<d3.Selection<SVGGElement, unknown, null, undefined> | null>(null);

  // Use refs to avoid stale closures in D3 event handlers
  const onSelectElementRef = useRef(onSelectElement);
  const onUpdateElementRef = useRef(onUpdateElement);
  const onContextMenuRef = useRef(onContextMenu);
  const onDoubleClickRef = useRef(onDoubleClick);
  const elementsRef = useRef(elements);
  const relationshipsRef = useRef(relationships);
  const tempPositionsRef = useRef<Map<string, { x: number; y: number }>>(new Map());

  useEffect(() => {
    onSelectElementRef.current = onSelectElement;
    onUpdateElementRef.current = onUpdateElement;
    onContextMenuRef.current = onContextMenu;
    onDoubleClickRef.current = onDoubleClick;
    elementsRef.current = elements;
    relationshipsRef.current = relationships;
  }, [onSelectElement, onUpdateElement, onContextMenu, onDoubleClick, elements, relationships]);

  // Find every element that should move when the given element moves
  const getCompositeDescendants = (elementId: string): ArchiMateElement[] => {
    const descendants: ArchiMateElement[] = [];
    const visited = new Set<string>();
    const queue = [elementId];
    const elementMap = new Map(elementsRef.current.map(el => [el.id, el]));

    while (queue.length > 0) {
      const currentId = queue.pop()!;
      const currentElement = elementMap.get(currentId);
      if (!currentElement) {
        continue;
      }

      const addChild = (candidateId: string, requireContainment: boolean) => {
        if (visited.has(candidateId) || candidateId === currentId) return;
        const candidate = elementMap.get(candidateId);
        if (!candidate) return;
        if (requireContainment && !elementContains(currentElement, candidate)) return;
        descendants.push(candidate);
        visited.add(candidate.id);
        queue.push(candidate.id);
      };

      for (const rel of relationshipsRef.current) {
        const isCompositeRel = rel.type === 'composition' || rel.type === 'aggregation';
        if (!isCompositeRel) {
          continue;
        }

        if (rel.sourceId === currentId) {
          addChild(rel.targetId, false);
        } else if (rel.targetId === currentId) {
          // If the relationship direction is reversed, only treat it as nested
          // when the current element actually contains the other element.
          addChild(rel.sourceId, true);
        }
      }

      // Any element can act as a container when another element is placed within it.
      for (const candidate of elementsRef.current) {
        if (candidate.id === currentId || visited.has(candidate.id)) continue;
        if (elementContains(currentElement, candidate)) {
          descendants.push(candidate);
          visited.add(candidate.id);
          queue.push(candidate.id);
        }
      }
    }

    return descendants;
  };

  // Helper: Update relationship lines connected to an element
  const updateConnectedLines = (elementId: string, newX: number, newY: number, newWidth?: number, newHeight?: number) => {
    if (!gRefForUpdate.current) return;

    try {
      const currentElements = elementsRef.current;
      const currentRelationships = relationshipsRef.current;

      // Find relationships connected to this element
      currentRelationships.forEach((rel) => {
        if (rel.sourceId === elementId || rel.targetId === elementId) {
          const sourceOverride = tempPositionsRef.current.get(rel.sourceId);
          const targetOverride = tempPositionsRef.current.get(rel.targetId);
          const sourceBase = currentElements.find(e => e.id === rel.sourceId);
          const targetBase = currentElements.find(e => e.id === rel.targetId);

          const source = rel.sourceId === elementId
            ? { ...sourceBase!, x: newX, y: newY, width: newWidth, height: newHeight }
            : sourceOverride
              ? { ...sourceBase!, x: sourceOverride.x, y: sourceOverride.y }
              : sourceBase;
          const target = rel.targetId === elementId
            ? { ...targetBase!, x: newX, y: newY, width: newWidth, height: newHeight }
            : targetOverride
              ? { ...targetBase!, x: targetOverride.x, y: targetOverride.y }
              : targetBase;

          if (source && target && source.x != null && target.x != null) {
            // Calculate edge points
            const sourceW = (source.width || 120) / 2;
            const sourceH = (source.height || 60) / 2;
            const targetW = (target.width || 120) / 2;
            const targetH = (target.height || 60) / 2;

            const dx = target.x - source.x;
            const dy = target.y - source.y;
            const angle = Math.atan2(dy, dx);

            const sourceEdge = getBoxEdgePoint(source.x, source.y, sourceW, sourceH, angle);
            const targetEdge = getBoxEdgePoint(target.x, target.y, targetW, targetH, angle + Math.PI);

            // Update lines with matching data-rel-id
            if (gRefForUpdate.current) {
              gRefForUpdate.current
                .select('.relationships')
                .selectAll(`line[data-rel-id="${rel.id}"]`)
                .attr('x1', sourceEdge.x)
                .attr('y1', sourceEdge.y)
                .attr('x2', targetEdge.x)
                .attr('y2', targetEdge.y);
            }
          }
        }
      });
    } catch (e) {
      // Silently fail - will be corrected on next full render
    }
  };

  useEffect(() => {
    if (!svgRef.current || !gRef.current) return;

    const svg = d3.select(svgRef.current);
    const g = d3.select(gRef.current);
    gRefForUpdate.current = g;

    const width = svgRef.current.clientWidth;
    const height = svgRef.current.clientHeight;

    // Zoom behavior
    const zoom = d3.zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.1, 4])
      .on('zoom', (event) => {
        transformRef.current = event.transform;
        g.attr('transform', event.transform.toString());
      });

    svg.call(zoom);
    svg.call(zoom.transform, transformRef.current);

    // Clear and redraw
    g.selectAll('*').remove();

    // Draw relationships with proper ArchiMate styling
    const relGroup = g.append('g').attr('class', 'relationships');

    const getRelDescription = (rel: ArchiMateRelationship, source: ArchiMateElement, target: ArchiMateElement) => {
      const sourceName = source.name || source.id;
      const targetName = target.name || target.id;
      switch (rel.type) {
        case 'composition':
          return `${sourceName} is composed of ${targetName}`;
        case 'aggregation':
          return `${sourceName} aggregates ${targetName}`;
        case 'assignment':
          return `${sourceName} is assigned to ${targetName}`;
        case 'realization':
          return `${sourceName} realizes ${targetName}`;
        case 'serving':
          return `${sourceName} serves ${targetName}`;
        case 'access':
          return `${sourceName} accesses ${targetName}`;
        case 'influence':
          return `${sourceName} influences ${targetName}`;
        case 'association':
          return `${sourceName} is associated with ${targetName}`;
        case 'triggering':
          return `${sourceName} triggers ${targetName}`;
        case 'flow':
          return `${sourceName} flows to ${targetName}`;
        case 'specialization':
          return `${sourceName} specializes ${targetName}`;
        default:
          return `${sourceName} connects to ${targetName}`;
      }
    };

    relationships.forEach((rel) => {
      const source = elements.find(e => e.id === rel.sourceId);
      const target = elements.find(e => e.id === rel.targetId);

      if (source && target) {
        // Determine line style based on relationship type
        const lineStyle = getRelationshipStyle(rel.type);
        const description = getRelDescription(rel, source, target);

        // Calculate edge intersection points
        const sourceW = (source.width || 120) / 2;
        const sourceH = (source.height || 60) / 2;
        const targetW = (target.width || 120) / 2;
        const targetH = (target.height || 60) / 2;

        // Angle from source to target
        const dx = target.x - source.x;
        const dy = target.y - source.y;
        const angle = Math.atan2(dy, dx);

        // Source edge point
        const sourceEdge = getBoxEdgePoint(source.x, source.y, sourceW, sourceH, angle);
        // Target edge point (opposite direction)
        const targetEdge = getBoxEdgePoint(target.x, target.y, targetW, targetH, angle + Math.PI);

        // Invisible wider line for easier clicking
        relGroup.append('line')
          .attr('data-rel-id', rel.id)
          .attr('data-rel-type', 'hitbox')
          .attr('x1', sourceEdge.x)
          .attr('y1', sourceEdge.y)
          .attr('x2', targetEdge.x)
          .attr('y2', targetEdge.y)
          .attr('stroke', 'transparent')
          .attr('stroke-width', 12)
          .style('cursor', 'pointer')
          .on('mouseenter', function(event) {
            d3.select(this)
              .attr('stroke', 'rgba(255,255,255,0.1)');

            relGroup.append('text')
              .attr('data-rel-tooltip', rel.id)
              .attr('x', (sourceEdge.x + targetEdge.x) / 2 + 10)
              .attr('y', (sourceEdge.y + targetEdge.y) / 2 - 10)
              .attr('fill', '#fff')
              .attr('font-size', 12)
              .attr('font-family', 'Inter, sans-serif')
              .attr('pointer-events', 'none')
              .attr('paint-order', 'stroke')
              .attr('stroke', '#0A1E2E')
              .attr('stroke-width', 4)
              .text(description);
          })
          .on('mouseleave', function() {
            d3.select(this)
              .attr('stroke', 'transparent');
            relGroup.selectAll(`text[data-rel-tooltip="${rel.id}"]`).remove();
          })
          .on('contextmenu', function(event) {
            event.preventDefault();
            event.stopPropagation();
            onContextMenuRef.current?.(event.clientX, event.clientY, 'relationship', rel.id);
          });

        // Visible line
        relGroup.append('line')
          .attr('data-rel-id', rel.id)
          .attr('data-rel-type', 'visible')
          .attr('x1', sourceEdge.x)
          .attr('y1', sourceEdge.y)
          .attr('x2', targetEdge.x)
          .attr('y2', targetEdge.y)
          .attr('stroke', '#E89841')
          .attr('stroke-width', 2)
          .attr('stroke-dasharray', lineStyle.dashArray)
          .attr('marker-end', `url(#${lineStyle.marker})`)
          .style('pointer-events', 'none');
      }
    });

    // Arrow markers (different types for ArchiMate)
    let defs = svg.select<SVGDefsElement>('defs');
    if (defs.empty()) {
      defs = svg.append<SVGDefsElement>('defs');
    }
    defs.selectAll('marker').remove();

    // Open arrow (standard) - orange
    defs.append('marker')
      .attr('id', 'arrow-open')
      .attr('viewBox', '0 -5 10 10')
      .attr('refX', 8)
      .attr('refY', 0)
      .attr('markerWidth', 6)
      .attr('markerHeight', 6)
      .attr('orient', 'auto')
      .append('path')
      .attr('d', 'M 0,-5 L 10,0 L 0,5')
      .attr('fill', 'none')
      .attr('stroke', '#E89841')
      .attr('stroke-width', 2);

    // Filled arrow (composition) - orange
    defs.append('marker')
      .attr('id', 'arrow-filled')
      .attr('viewBox', '0 -5 10 10')
      .attr('refX', 8)
      .attr('refY', 0)
      .attr('markerWidth', 6)
      .attr('markerHeight', 6)
      .attr('orient', 'auto')
      .append('path')
      .attr('d', 'M 0,-5 L 10,0 L 0,5 Z')
      .attr('fill', '#E89841');

    // Hollow arrow (aggregation/specialization) - orange with white fill
    defs.append('marker')
      .attr('id', 'arrow-hollow')
      .attr('viewBox', '0 -5 10 10')
      .attr('refX', 8)
      .attr('refY', 0)
      .attr('markerWidth', 6)
      .attr('markerHeight', 6)
      .attr('orient', 'auto')
      .append('path')
      .attr('d', 'M 0,-5 L 10,0 L 0,5 Z')
      .attr('fill', '#0A1E2E')
      .attr('stroke', '#E89841')
      .attr('stroke-width', 2);

    // Draw elements
    const elemGroup = g.append('g').attr('class', 'elements');
    const parentMap = new Map<string, Set<string>>();
    relationships.forEach((rel) => {
      if (rel.type === 'composition' || rel.type === 'aggregation') {
        const parents = parentMap.get(rel.targetId) ?? new Set<string>();
        parents.add(rel.sourceId);
        parentMap.set(rel.targetId, parents);
      }
    });

    const depthCache = new Map<string, number>();
    const getDepth = (elementId: string, visited = new Set<string>()): number => {
      if (depthCache.has(elementId)) {
        return depthCache.get(elementId)!;
      }
      if (visited.has(elementId)) {
        return 0;
      }
      visited.add(elementId);
      let maxDepth = 0;
      const parents = parentMap.get(elementId);
      if (parents) {
        parents.forEach((parentId) => {
          maxDepth = Math.max(maxDepth, 1 + getDepth(parentId, visited));
        });
      }
      depthCache.set(elementId, maxDepth);
      return maxDepth;
    };

    const orderedElements = [...elements].sort((a, b) => {
      const depthDiff = getDepth(a.id) - getDepth(b.id);
      if (depthDiff !== 0) {
        return depthDiff;
      }
      if (elementContains(a, b)) return -1;
      if (elementContains(b, a)) return 1;
      return a.id.localeCompare(b.id);
    });

    orderedElements.forEach((element) => {
      const metadata = getElementMetadata(element.type);
      if (!metadata) return;

      const elemG = elemGroup.append('g')
        .attr('data-element-id', element.id) // Add data attribute for lookup
        .attr('transform', `translate(${element.x}, ${element.y})`)
        .style('cursor', 'move');

      const isPrimarySelected = selectedElement?.id === element.id;
      const isMultiSelected = selectedElementIds.includes(element.id);
      const isSelected = isPrimarySelected || isMultiSelected;
      const width = element.width || 120;
      const height = element.height || 60;
      const strokeColor = isSelected ? '#E89841' : '#333';
      const strokeWidth = isSelected ? 2.5 : 1.5;

      // Calculate element fill color - use heatmap color if active, otherwise use default
      let fillColor = metadata.color;
      if (activeHeatmap) {
        const heatmapColor = getHeatmapColor(element.id, activeHeatmap, elementMetadata);
        if (heatmapColor) {
          fillColor = heatmapColor;
        }
      }

      // Main shape based on ArchiMate standard
      switch (metadata.shape) {
        case 'box':
          elemG.append('rect')
            .attr('x', -width / 2)
            .attr('y', -height / 2)
            .attr('width', width)
            .attr('height', height)
            .attr('fill', fillColor)
            .attr('stroke', strokeColor)
            .attr('stroke-width', strokeWidth);
          break;

        case 'rounded':
          elemG.append('rect')
            .attr('x', -width / 2)
            .attr('y', -height / 2)
            .attr('width', width)
            .attr('height', height)
            .attr('fill', fillColor)
            .attr('stroke', strokeColor)
            .attr('stroke-width', strokeWidth)
            .attr('rx', 12);
          break;

        case 'pill':
          elemG.append('rect')
            .attr('x', -width / 2)
            .attr('y', -height / 2)
            .attr('width', width)
            .attr('height', height)
            .attr('fill', fillColor)
            .attr('stroke', strokeColor)
            .attr('stroke-width', strokeWidth)
            .attr('rx', height / 2); // Full pill shape
          break;

        case 'pentagon':
          // Arrow/pentagon shape for events
          const points = [
            `${-width/2},${-height/2}`,
            `${width/2 - 15},${-height/2}`,
            `${width/2},${0}`,
            `${width/2 - 15},${height/2}`,
            `${-width/2},${height/2}`,
          ].join(' ');
          elemG.append('polygon')
            .attr('points', points)
            .attr('fill', fillColor)
            .attr('stroke', strokeColor)
            .attr('stroke-width', strokeWidth);
          break;

        case 'cube':
          // 3D cube effect
          // Front face
          elemG.append('rect')
            .attr('x', -width / 2)
            .attr('y', -height / 2 + 8)
            .attr('width', width)
            .attr('height', height)
            .attr('fill', fillColor)
            .attr('stroke', strokeColor)
            .attr('stroke-width', strokeWidth);
          // Top face
          elemG.append('polygon')
            .attr('points', `${-width/2},${-height/2+8} ${-width/2+8},${-height/2} ${width/2+8},${-height/2} ${width/2},${-height/2+8}`)
            .attr('fill', adjustColor(fillColor, 20))
            .attr('stroke', strokeColor)
            .attr('stroke-width', strokeWidth);
          // Right face
          elemG.append('polygon')
            .attr('points', `${width/2},${-height/2+8} ${width/2+8},${-height/2} ${width/2+8},${height/2} ${width/2},${height/2+8}`)
            .attr('fill', adjustColor(fillColor, -20))
            .attr('stroke', strokeColor)
            .attr('stroke-width', strokeWidth);
          break;

        case 'folded':
          // Box with folded corner (like document)
          const foldSize = 10;
          elemG.append('path')
            .attr('d', `
              M ${-width/2} ${-height/2}
              L ${width/2 - foldSize} ${-height/2}
              L ${width/2} ${-height/2 + foldSize}
              L ${width/2} ${height/2}
              L ${-width/2} ${height/2}
              Z
            `)
            .attr('fill', fillColor)
            .attr('stroke', strokeColor)
            .attr('stroke-width', strokeWidth);
          // Fold line
          elemG.append('polyline')
            .attr('points', `${width/2 - foldSize},${-height/2} ${width/2 - foldSize},${-height/2 + foldSize} ${width/2},${-height/2 + foldSize}`)
            .attr('fill', 'none')
            .attr('stroke', strokeColor)
            .attr('stroke-width', strokeWidth);
          break;

        case 'wavy':
          // Box with wavy bottom
          elemG.append('path')
            .attr('d', `
              M ${-width/2} ${-height/2}
              L ${width/2} ${-height/2}
              L ${width/2} ${height/2 - 5}
              Q ${width/4} ${height/2 + 5} ${0} ${height/2 - 5}
              Q ${-width/4} ${height/2 - 15} ${-width/2} ${height/2 - 5}
              Z
            `)
            .attr('fill', fillColor)
            .attr('stroke', strokeColor)
            .attr('stroke-width', strokeWidth);
          break;

        case 'circle':
          // Small circle for junction
          const radius = 15;
          elemG.append('circle')
            .attr('r', radius)
            .attr('fill', fillColor)
            .attr('stroke', strokeColor)
            .attr('stroke-width', strokeWidth);
          break;
      }

      // Icon in top-right corner (ArchiMate standard position)
      // Adjust position based on shape
      let iconX = width / 2 - 16;
      let iconY = -height / 2 + 4;

      if (metadata.shape === 'cube') {
        // Icon on front face of cube
        iconY = -height / 2 + 12;
      } else if (metadata.shape === 'pentagon') {
        // Icon further left to avoid arrow point
        iconX = width / 2 - 30;
        iconY = -height / 2 + 8;
      } else if (metadata.shape === 'pill') {
        // Icon further left to stay within pill curve
        iconX = width / 2 - 24;
        iconY = -height / 2 + 8;
      }

      // Only render if icon is specified
      if (metadata.icon) {
        renderArchiMateIcon(elemG, element.type, iconX, iconY, 14, metadata.icon);
      }

      // Element name (top-aligned for larger boxes, centered for small)
      const lines = wrapText(element.name || metadata.name, width - 24);
      const textStartY = height > 80 ? -height / 2 + 20 : -(lines.length - 1) * 6;

      lines.forEach((line, i) => {
        elemG.append('text')
          .text(line)
          .attr('text-anchor', 'middle')
          .attr('y', textStartY + i * 12)
          .attr('dy', 4)
          .attr('fill', '#000')
          .attr('font-size', '11px')
          .attr('font-family', 'Arial, sans-serif')
          .style('pointer-events', 'none');
      });

      // Add interaction overlay (AFTER all visual elements, on top)
      const interactionRect = elemG.append('rect')
        .attr('class', 'interaction-overlay')
        .attr('x', -width / 2)
        .attr('y', -height / 2)
        .attr('width', width)
        .attr('height', height)
        .attr('fill', 'transparent')
        .attr('stroke', 'none') // Explicitly no stroke
        .attr('cursor', tool === 'select' ? 'move' : 'pointer')
        .style('pointer-events', 'all');

      // Context menu - attach to group so it works in both modes
      elemG.on('contextmenu', function(event) {
        event.preventDefault();
        event.stopPropagation();
        onContextMenuRef.current?.(event.clientX, event.clientY, 'element', element.id);
      });

      // Double-click
      interactionRect.on('dblclick.edit', function(event) {
        event.preventDefault();
        event.stopPropagation();
        onDoubleClickRef.current?.(element);
      });

      // Single click
      interactionRect.on('click.select', function(event) {
        event.stopPropagation();
        onSelectElementRef.current?.(element);
      });

      // Make draggable - only in select mode, attach to GROUP
      if (tool === 'select') {
        let dragStarted = false;
        let draggedChildren: {
          element: ArchiMateElement;
          selection: d3.Selection<SVGGElement, unknown, null, undefined>;
        }[] = [];

        elemG.call(d3.drag<any, any>()
          .on('start', function() {
            dragStarted = false;
            d3.select(this).raise(); // Bring to front

            // Cache descendants and their nodes so we can move them together
            draggedChildren = getCompositeDescendants(element.id)
              .map(child => ({
                element: child,
                selection: g.select<SVGGElement>(`[data-element-id="${child.id}"]`),
              }))
              .filter(({ selection }) => !selection.empty());

            // Ensure children stay visible above the parent while dragging
            draggedChildren.forEach(({ selection }) => {
              selection.raise();
            });

            tempPositionsRef.current.set(element.id, { x: element.x, y: element.y });
            draggedChildren.forEach(({ element: child }) => {
              tempPositionsRef.current.set(child.id, { x: child.x, y: child.y });
            });
          })
          .on('drag', function(event) {
            if (!dragStarted) {
              dragStarted = true;
            }
            // Use dx/dy for relative movement
            const currentTransform = d3.select(this).attr('transform');
            const match = currentTransform?.match(/translate\(([^,]+),([^)]+)\)/);
              if (match) {
                const currentX = parseFloat(match[1]);
                const currentY = parseFloat(match[2]);
                const newX = currentX + event.dx;
                const newY = currentY + event.dy;
                d3.select(this).attr('transform', `translate(${newX}, ${newY})`);
                tempPositionsRef.current.set(element.id, { x: newX, y: newY });

              // Move all descendant elements by the same delta
              draggedChildren.forEach(({ element: child, selection }) => {
                const childTransform = selection.attr('transform');
                const childMatch = childTransform?.match(/translate\(([^,]+),([^)]+)\)/);
                if (!childMatch) return;
                const childX = parseFloat(childMatch[1]) + event.dx;
                const childY = parseFloat(childMatch[2]) + event.dy;
                selection.attr('transform', `translate(${childX}, ${childY})`);
                tempPositionsRef.current.set(child.id, { x: childX, y: childY });
                updateConnectedLines(child.id, childX, childY);
              });

              // Update connected relationships (throttled via requestAnimationFrame)
              if (!(this as any).__rafPending) {
                (this as any).__rafPending = true;
                requestAnimationFrame(() => {
                  const match2 = d3.select(this).attr('transform')?.match(/translate\(([^,]+),([^)]+)\)/);
                  if (match2) {
                    updateConnectedLines(element.id, parseFloat(match2[1]), parseFloat(match2[2]));
                  }
                  (this as any).__rafPending = false;
                });
              }
            }
          })
          .on('end', function(event) {
            if (dragStarted) {
              // Save final position only if we actually dragged
              const currentTransform = d3.select(this).attr('transform');
              const match = currentTransform?.match(/translate\(([^,]+),([^)]+)\)/);
              if (match) {
                const finalX = parseFloat(match[1]);
                const finalY = parseFloat(match[2]);
                onUpdateElementRef.current?.(element.id, { x: finalX, y: finalY });
              }

              // Persist the final positions of any children we dragged
              draggedChildren.forEach(({ element: child, selection }) => {
                const childTransform = selection.attr('transform');
                const childMatch = childTransform?.match(/translate\(([^,]+),([^)]+)\)/);
                if (!childMatch) return;
                const childX = parseFloat(childMatch[1]);
                const childY = parseFloat(childMatch[2]);
                onUpdateElementRef.current?.(child.id, { x: childX, y: childY });
              });

              tempPositionsRef.current.delete(element.id);
              draggedChildren.forEach(({ element: child }) => {
                tempPositionsRef.current.delete(child.id);
              });
            } else {
              // Was just a click, not a drag - trigger select
              onSelectElementRef.current?.(element);
            }
          })
        );
      }

      // Keep interaction rect enabled always for clicks (even in select mode)
      interactionRect.style('pointer-events', 'all');

      // Highlight if pending connection source (dashed circle, not box)
      if (pendingConnection === element.id) {
        elemG.append('circle')
          .attr('r', Math.max(width, height) / 2 + 10)
          .attr('fill', 'none')
          .attr('stroke', '#E89841')
          .attr('stroke-width', 2)
          .attr('stroke-dasharray', '5,5')
          .attr('opacity', 0.6)
          .style('pointer-events', 'none'); // Don't interfere with interaction
      }

      // Resize handles if selected
      if (isPrimarySelected && tool === 'select') {
        const handleSize = 8;
        const handles = [
          { pos: 'se', x: width/2, y: height/2, cursor: 'nwse-resize' },
          { pos: 'sw', x: -width/2, y: height/2, cursor: 'nesw-resize' },
          { pos: 'ne', x: width/2, y: -height/2, cursor: 'nesw-resize' },
          { pos: 'nw', x: -width/2, y: -height/2, cursor: 'nwse-resize' },
        ];

        handles.forEach(handle => {
          const resizeHandle = elemG.append('rect')
            .attr('x', handle.x - handleSize/2)
            .attr('y', handle.y - handleSize/2)
            .attr('width', handleSize)
            .attr('height', handleSize)
            .attr('fill', '#E89841')
            .attr('stroke', '#fff')
            .attr('stroke-width', 1)
            .style('cursor', handle.cursor);

          // Store final resize values
          let finalWidth = element.width || 120;
          let finalHeight = element.height || 60;
          let finalX = element.x;
          let finalY = element.y;

          resizeHandle.call(d3.drag<any, any>()
            .on('start', function(event) {
              // Capture starting state
              finalWidth = element.width || 120;
              finalHeight = element.height || 60;
              finalX = element.x;
              finalY = element.y;
            })
            .on('drag', function(event) {
              // Calculate drag distance (event.dx/dy are incremental per frame)
              const dx = event.dx;
              const dy = event.dy;

              // Update size based on which corner
              if (handle.pos === 'se') {
                finalWidth = Math.max(60, finalWidth + dx);
                finalHeight = Math.max(40, finalHeight + dy);
                finalX += dx / 2;
                finalY += dy / 2;
              } else if (handle.pos === 'sw') {
                finalWidth = Math.max(60, finalWidth - dx); // dx negative, so width increases
                finalHeight = Math.max(40, finalHeight + dy);
                finalX += dx / 2; // dx negative, so moves left ✓
                finalY += dy / 2;
              } else if (handle.pos === 'ne') {
                finalWidth = Math.max(60, finalWidth + dx);
                finalHeight = Math.max(40, finalHeight - dy); // dy negative, so height increases
                finalX += dx / 2;
                finalY += dy / 2; // dy negative, so moves up ✓
              } else if (handle.pos === 'nw') {
                finalWidth = Math.max(60, finalWidth - dx);
                finalHeight = Math.max(40, finalHeight - dy);
                finalX += dx / 2; // CHANGED from -= to +=
                finalY += dy / 2; // CHANGED from -= to +=
              }

              // Update the group transform immediately (no state update)
              elemG.attr('transform', `translate(${finalX}, ${finalY})`);

              // Update main shape based on type
              if (metadata.shape === 'pentagon') {
                // Update pentagon/arrow shape
                const points = [
                  `${-finalWidth/2},${-finalHeight/2}`,
                  `${finalWidth/2 - 15},${-finalHeight/2}`,
                  `${finalWidth/2},${0}`,
                  `${finalWidth/2 - 15},${finalHeight/2}`,
                  `${-finalWidth/2},${finalHeight/2}`,
                ].join(' ');
                elemG.select('polygon').attr('points', points);
              } else if (metadata.shape === 'circle') {
                // Update circle radius
                elemG.select('circle').attr('r', 15);
              } else {
                // Update rectangles (box, rounded, pill, cube faces, etc.)
                elemG.selectAll(':scope > rect').each(function() {
                  const shape = d3.select(this);
                  const className = shape.attr('class') || '';
                  const cursor = shape.style('cursor');

                  if (className === 'interaction-overlay') {
                    // Update interaction overlay
                    shape
                      .attr('x', -finalWidth / 2)
                      .attr('y', -finalHeight / 2)
                      .attr('width', finalWidth)
                      .attr('height', finalHeight);
                  } else if (!cursor || !cursor.includes('resize')) {
                    // Update main shape
                    shape
                      .attr('x', -finalWidth / 2)
                      .attr('y', -finalHeight / 2)
                      .attr('width', finalWidth)
                      .attr('height', finalHeight);
                  }
                });
              }

              // Update resize handle positions (direct children only)
              elemG.selectAll(':scope > rect').each(function() {
                const shape = d3.select(this);
                const cursor = shape.style('cursor');
                if (cursor && cursor.includes('nwse-resize')) {
                  // SE or NW corner
                  const xAttr = parseFloat(shape.attr('x'));
                  const isSE = xAttr > 0;
                  shape.attr('x', (isSE ? finalWidth/2 : -finalWidth/2) - handleSize/2)
                    .attr('y', (isSE ? finalHeight/2 : -finalHeight/2) - handleSize/2);
                } else if (cursor && cursor.includes('nesw-resize')) {
                  // SW or NE corner
                  const xAttr = parseFloat(shape.attr('x'));
                  const isSW = xAttr < 0;
                  shape.attr('x', (isSW ? -finalWidth/2 : finalWidth/2) - handleSize/2)
                    .attr('y', (isSW ? finalHeight/2 : -finalHeight/2) - handleSize/2);
                }
              });

              // Update text position (top-aligned for tall boxes)
              const textStartY = finalHeight > 80 ? -finalHeight / 2 + 20 : 0;
              elemG.selectAll('text').each(function(d, i) {
                const text = d3.select(this);
                // Only update element name text (not icon text)
                if (text.attr('font-size') === '11px') {
                  text.attr('y', textStartY + i * 12);
                }
              });

              // Update icon position (only target icon group with class)
              elemG.select('.element-icon').attr('transform', function() {
                const iconX = metadata.shape === 'cube' ? finalWidth / 2 - 16 :
                             metadata.shape === 'pentagon' ? finalWidth / 2 - 30 :
                             metadata.shape === 'pill' ? finalWidth / 2 - 24 :
                             finalWidth / 2 - 16;
                const iconY = metadata.shape === 'cube' ? -finalHeight / 2 + 12 :
                             metadata.shape === 'pentagon' ? -finalHeight / 2 + 8 :
                             metadata.shape === 'pill' ? -finalHeight / 2 + 8 :
                             -finalHeight / 2 + 4;
                return `translate(${iconX}, ${iconY})`;
              });
            })
            .on('end', () => {
              // Save final state
              onUpdateElementRef.current?.(element.id, {
                width: finalWidth,
                height: finalHeight,
                x: finalX,
                y: finalY,
              });
            })
          );
        });
      }
    });

    // Canvas click to deselect and close menus
    svg.on('click', () => {
      onSelectElementRef.current?.(null as any);
    });

    // Global dblclick handler on SVG (to bypass D3 drag interference)
    svg.on('dblclick', function(event: any) {
      // Find which element was double-clicked
      const target = event.target;
      const elementGroup = d3.select(target).node()?.closest('[data-element-id]') as any;
      if (elementGroup) {
        const elementId = elementGroup.getAttribute('data-element-id');
        const element = elements.find(e => e.id === elementId);
        if (element) {
          event.preventDefault();
          event.stopPropagation();
          onDoubleClickRef.current?.(element);
        }
      }
    });

  }, [elements, relationships, selectedElement, selectedElementIds, pendingConnection, tool, activeHeatmap, elementMetadata]);

  // Handle drop on canvas
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (!svgRef.current || !gRef.current) return;

    // Check if it's an element type (from palette) or a component (from library)
    const elementType = e.dataTransfer.getData('elementType');
    const componentData = e.dataTransfer.getData('component');

    if (!elementType && !componentData) return;

    // Get drop coordinates relative to SVG
    const svg = svgRef.current;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const svgP = pt.matrixTransform(svg.getScreenCTM()?.inverse());

    // Transform to canvas coordinates
    const transform = transformRef.current;
    const x = (svgP.x - transform.x) / transform.k;
    const y = (svgP.y - transform.y) / transform.k;

    // Check if dropped on an existing element
    let targetElementId: string | undefined;
    for (const element of elements) {
      const halfW = (element.width || 120) / 2;
      const halfH = (element.height || 60) / 2;

      if (
        x >= element.x - halfW &&
        x <= element.x + halfW &&
        y >= element.y - halfH &&
        y <= element.y + halfH
      ) {
        targetElementId = element.id;
        break;
      }
    }

    if (elementType) {
      // New element from palette
      onCanvasDrop?.(elementType, x, y, targetElementId);
    } else if (componentData) {
      // Existing component from library
      onCanvasDrop?.(componentData, x, y, targetElementId);
    }
  };

  return (
    <svg
      id="archimate-canvas"
      ref={svgRef}
      className="w-full h-full"
      style={{ background: '#0A1E2E' }}
      onDrop={handleDrop}
      onDragOver={(e) => e.preventDefault()}
    >
      <defs />
      <g ref={gRef} />
    </svg>
  );
}
